import React from 'react';
import {Modal, ScrollView, StyleSheet, Text, TouchableOpacity, TouchableWithoutFeedback, View, useWindowDimensions} from 'react-native';
import {Ionicons} from '@expo/vector-icons';
import {useTheme} from '../../helpers/ThemeContext';
import {useBottomInset} from '../../helpers/safeArea';
import {haptics} from '../../helpers/haptics';
import {POS_MIN_TOUCH} from '../../helpers/posLayout';

/**
 * Phone bottom sheet for POS flows: solid themed surface with a grab handle,
 * a title row with a 52dp close target, scrollable body and safe-area padding.
 * (Built on the plain RN `Modal`, like the app's other premium sheets — never a
 * default dialog.)
 *
 * @param {object} props
 * @param {boolean} props.visible Whether the sheet is shown.
 * @param {function(): void} props.onClose Scrim tap / back / close button.
 * @param {string} [props.title] Heading.
 * @param {string} [props.subtitle] Supporting line.
 * @param {React.ReactNode} props.children Sheet body (placed inside a ScrollView).
 * @param {React.ReactNode} [props.footer] Pinned under the body (primary button row).
 * @param {number} [props.maxHeightRatio=0.8] Max share of the window height.
 * @returns {JSX.Element}
 */
const PosBottomSheet = ({visible, onClose, title, subtitle, children, footer, maxHeightRatio = 0.8}) => {
    const {theme} = useTheme();
    const bottomInset = useBottomInset();
    const {height} = useWindowDimensions();

    return (
        <Modal transparent visible={visible} animationType="slide" onRequestClose={onClose} statusBarTranslucent>
            <View style={styles.root}>
                <TouchableWithoutFeedback onPress={onClose} accessibilityLabel="close">
                    <View style={styles.scrim} />
                </TouchableWithoutFeedback>
                <View
                    style={[
                        styles.sheet,
                        {backgroundColor: theme.colors.card, maxHeight: height * maxHeightRatio, paddingBottom: 12 + bottomInset},
                    ]}
                >
                    <View style={[styles.handle, {backgroundColor: theme.colors.border}]} />
                    <View style={styles.titleRow}>
                        <View style={styles.titleText}>
                            {!!title && <Text style={[styles.title, {color: theme.colors.text}]} numberOfLines={1}>{title}</Text>}
                            {!!subtitle && <Text style={[styles.subtitle, {color: theme.colors.subText}]} numberOfLines={2}>{subtitle}</Text>}
                        </View>
                        <TouchableOpacity
                            onPress={() => {
                                haptics.select();
                                onClose();
                            }}
                            activeOpacity={0.8}
                            accessibilityRole="button"
                            accessibilityLabel="close"
                            style={styles.close}
                        >
                            <Ionicons name="close" size={26} color={theme.colors.subText} />
                        </TouchableOpacity>
                    </View>
                    <ScrollView style={styles.body} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
                        {children}
                    </ScrollView>
                    {footer}
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    root: {flex: 1, justifyContent: 'flex-end'},
    scrim: {...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(0,0,0,0.45)'},
    // Capped and centred so it does not stretch edge-to-edge on tablets / unfolded foldables.
    sheet: {
        width: '100%',
        maxWidth: 640,
        alignSelf: 'center',
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        paddingHorizontal: 16,
        paddingTop: 8,
    },
    handle: {alignSelf: 'center', width: 42, height: 5, borderRadius: 3, marginBottom: 6},
    titleRow: {flexDirection: 'row', alignItems: 'center', minHeight: POS_MIN_TOUCH},
    titleText: {flex: 1},
    title: {fontSize: 21, fontWeight: '900'},
    subtitle: {fontSize: 14, fontWeight: '600', marginTop: 2},
    close: {width: POS_MIN_TOUCH, height: POS_MIN_TOUCH, alignItems: 'center', justifyContent: 'center'},
    body: {flexGrow: 0},
});

export default PosBottomSheet;
